import React, { useState, useRef, useEffect } from "react";
import { Policy } from "@/shared/types/Quote.interface";
import { faDog, faCat, faPencil, faPaw } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button } from "@/shared/components/ui/Button";
import { Card, CardHeader, CardContent } from "@/shared/components/ui/Card";
import PetModal, { PetModalConfig } from "../PetModal";
import { useAppLayerContext } from "@/shared/contexts/AppLayer";
import { PriceInfo } from "../PriceInfo";
import { CoverageUtils } from "@/shared/utils/CoverageUtils";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { cn } from "@/shared/utils";
import { Badge } from "../ui";
import { useCoverageEditorContext } from "../CoverageEditor";
import LoaderWithText from "../LoaderWithText";
import Strings from "@/shared/utils/Strings.constants";
import { useThemeContext } from "@/shared/contexts/ThemeProvider";

interface PetCardProps {
    pet: Policy;
    isQuoteUpdating?: boolean;
    petModalConfig: PetModalConfig;
    quoteId: string;
    activePetId: string;
    policiesCount: number;
    handleActivePetChange: (value: string) => void;
    isMultiPetLinked?: boolean;
    isApplyAllHidden?: boolean;
    onSelect?: () => void;
    priceRef?: React.Ref<HTMLDivElement>;
}

const PetCard: React.FC<PetCardProps> = ({
    pet,
    isQuoteUpdating,
    petModalConfig,
    activePetId,
    policiesCount,
    handleActivePetChange,
    isMultiPetLinked,
    onSelect,
    isApplyAllHidden,
    priceRef
}) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const {
        appState: { quoteQuery, calculatedPriceData, editPetId, showEditPet, partnerOffer },
        updateAppState
    } = useAppLayerContext();
    const quote = quoteQuery?.data;
    const { includeTransactionFee, feeType, disableOptimisticDiscountPricing } = useCoverageEditorContext();
    const priceInfoData = CoverageUtils.getPriceInfoData({ value: quote, includeTransactionFee, feeType, petIdToShow: !isMultiPetLinked ? pet.id : undefined });
    const isActivePetCard = !!activePetId && activePetId === pet.id;
    // Only allow tabbing to active pet card, disable tabbing for "disabled / off screen" pet cards
    const activePetCardTabIndex = isActivePetCard ? 0 : -1;
    const isMultiPetActive = !isApplyAllHidden && isMultiPetLinked && policiesCount > 1;
    // A quote update is in flight with no optimistic price to show.
    const isPriceUpdatingWithoutOptimism = !!isQuoteUpdating && !calculatedPriceData;
    // Row-scoped, per-card loading (only when optimistic pricing is disabled): loader only on cards
    // the update affects — all cards when linked, otherwise just the active pet's. Other forms keep
    // the whole-card loader below.
    const isPriceCalculating = !!disableOptimisticDiscountPricing && isPriceUpdatingWithoutOptimism && (isMultiPetActive || isActivePetCard);
    const multiPetBg = policiesCount > 1;
    const touchStartTime = useRef<number | null>(null);
    const touchStartPosition = useRef<{ x: number; y: number } | null>(null);
    const { showRebrandTheme } = useThemeContext().theme;

    // edit pet in stepper test
    //
    // `editPetId` is a one-shot command from the Stepper's edit button: open this card's modal, then
    // clear the command so the next click (which sets the same id again) is seen as a fresh change.
    // Opening is a local state adjustment on a changed input, so it runs during render (React's
    // "adjusting state when a prop changes"); clearing writes to another component's state, so it
    // stays in an effect.
    // Seeded `undefined`, not `editPetId`: the coverage step unmounts its cards behind the pet-plans
    // loader, so a Stepper click during the loader lands on a card that MOUNTS with the command already
    // set. That first render must count as the transition, or the command is cleared below unseen.
    const [prevEditPetId, setPrevEditPetId] = useState<string | undefined>(undefined);
    if (editPetId !== prevEditPetId) {
        setPrevEditPetId(editPetId);
        if (editPetId === pet.id) {
            setIsModalOpen(true);
        }
    }

    useEffect(() => {
        if (editPetId === pet.id) {
            updateAppState({
                editPetId: undefined
            });
        }
    }, [editPetId, pet.id, updateAppState]);
    // test end

    if (!isMultiPetLinked && !pet.id) return null;

    const handleEditPet = (e: React.MouseEvent | React.TouchEvent | React.KeyboardEvent) => {
        setIsModalOpen(true);
    };

    const handleTouchStart = (e: React.TouchEvent) => {
        const touch = e.touches[0];
        if (touch) {
            touchStartTime.current = Date.now();
            touchStartPosition.current = { x: touch.clientX, y: touch.clientY };
        }
    };

    const handleTouchEnd = (e: React.TouchEvent) => {
        const touch = e.changedTouches[0];
        if (touchStartTime.current && touchStartPosition.current && touch) {
            const touchEndTime = Date.now();
            const touchEndPosition = { x: touch.clientX, y: touch.clientY };

            const touchDuration = touchEndTime - touchStartTime.current;
            const touchDistance = Math.sqrt(Math.pow(touchEndPosition.x - touchStartPosition.current.x, 2) + Math.pow(touchEndPosition.y - touchStartPosition.current.y, 2));

            // If the touch was short or long but didn't move much, consider it a tap or long press
            if (touchDistance < 10 || (touchDuration >= 500 && touchDistance < 20)) {
                handleEditPet(e);
            }
        }
    };

    const handleKeyUp = (e: React.KeyboardEvent) => {
        if (e.key === "Enter" || e.key === " ") {
            handleEditPet(e);
        }
    };

    const handleCardClick = () => {
        if (isQuoteUpdating) return;
        handleActivePetChange(pet?.id ?? activePetId);
        if (!!onSelect) {
            onSelect();
        }
        if (showEditPet && isActivePetCard) {
            setIsModalOpen(true);
        }
    };

    const handleCardKeyUp = (e: React.KeyboardEvent) => {
        if (e.key === "Enter" || e.key === " ") {
            handleCardClick();
        }
    };
    const handleCloseModal = (editedPetId?: string) => {
        setIsModalOpen(false);
        // If a pet was edited and we have an onSelect callback, trigger focus
        if (editedPetId && onSelect && editedPetId === pet.id) {
            handleActivePetChange(editedPetId);
            onSelect();
        }
    };

    const getIcon = (): IconProp => {
        if (isMultiPetLinked && policiesCount > 1) {
            return faPaw;
        }
        if (pet.species === "Cat") {
            return faCat;
        }
        return faDog;
    };

    // styles
    const transitionClass = "transition-opacity duration-200 ease-in-out";

    const cardContentClass = {
        "bg-background-primary": multiPetBg || showRebrandTheme,
        "dark:bg-background-secondary": isMultiPetLinked
    };

    return (
        <div className={cn("group relative flex size-full", showEditPet && isActivePetCard && "transition-transform duration-300 ease-out hover:-translate-y-1.5")}>
            <PetModal
                initialValues={{ policies: isMultiPetActive ? quote?.policies : [pet] }}
                triggerButton={
                    <Button
                        // No spinner when optimistic pricing is disabled — the price row already shows
                        // the loader; the button just disables.
                        loading={!disableOptimisticDiscountPricing && isQuoteUpdating}
                        disabled={isQuoteUpdating}
                        loadingSpinnerPosition="start"
                        onClick={handleEditPet}
                        onTouchStart={handleTouchStart}
                        onTouchEnd={handleTouchEnd}
                        onKeyUp={handleKeyUp}
                        variant="ghost"
                        className={cn(
                            "absolute right-3 top-3 z-10 h-8 shrink-0 rounded-full py-0 text-xs font-medium leading-5 text-content-primary-invert hover:bg-background-secondary-invert focus:absolute dark:hover:bg-background-overlay",
                            showEditPet && "hover:bg-background-brand-b-primary focus:rounded-full max-sm:bg-background-brand-b-primary",
                            showEditPet && isActivePetCard && "group-hover:bg-background-brand-b-primary group-hover:text-content-primary-invert"
                        )}
                        startDecorator={<FontAwesomeIcon icon={faPencil} className="size-5" />}
                        data-testid={`${isMultiPetActive ? "edit-pets-btn" : "edit-pet-btn"}`}
                        tabIndex={activePetCardTabIndex}
                    >
                        {isMultiPetActive ? Strings.EDIT_PETS : Strings.EDIT_PET}
                    </Button>
                }
                isOpen={isModalOpen}
                handleOpen={() => setIsModalOpen(true)}
                onClose={handleCloseModal}
                config={petModalConfig}
                handleActivePetChange={handleActivePetChange}
            />
            <button
                type="button"
                onClick={handleCardClick}
                onKeyUp={handleCardKeyUp}
                className={cn(
                    "flex h-full w-full flex-col overflow-hidden !rounded-3xl border-0 bg-content-primary-invert text-left sm:w-[328px]",
                    transitionClass,
                    policiesCount > 1 && "shadow-md",
                    "focus:shadow-none focus:outline-none",
                    // edit pet in stepper test: resting shadow-md, lifting to shadow-lg with the card hover
                    // (Figma single/multi default + hover states). `focus:shadow-md` replaces the
                    // `focus:shadow-none` above (tailwind-merge keeps the last) so the card does not go flat
                    // while it holds focus after the modal closes; the ring stays suppressed as before.
                    showEditPet && "shadow-md transition-[opacity,box-shadow] duration-300 ease-out focus:shadow-md",
                    showEditPet && isActivePetCard && "group-hover:shadow-lg"
                )}
                tabIndex={activePetCardTabIndex}
            >
                <Card
                    className={cn(
                        "size-full overflow-hidden !rounded-3xl",
                        !isMultiPetLinked && !isActivePetCard && "opacity-50 group-hover:opacity-100",
                        // `group-hover`, not `hover`: the Edit pill is an absolutely positioned sibling of this
                        // card, so a plain hover dropped the border while the pointer was on the pill.
                        showEditPet && isActivePetCard && "border-2 border-stroke-transparent group-hover:border-stroke-brand-b",
                        partnerOffer && "border-[2px]"
                    )}
                >
                    <CardHeader className="flex shrink-0 flex-row items-center justify-between whitespace-nowrap border-0 bg-background-primary-invert p-3 text-content-primary-invert">
                        <div className="flex min-w-0 flex-1 items-center gap-2">
                            <div className="flex size-8 items-center justify-center">
                                <FontAwesomeIcon icon={getIcon()} className="size-6 shrink-0" />
                            </div>

                            <span className="truncate font-bold" data-testid="policy-count">
                                {isMultiPetActive ? `${policiesCount} Pets` : pet.name}
                            </span>
                            {/* Placeholder element needed for text truncate to work properly. */}
                            <div className="ml-auto w-24 shrink-0"></div>
                        </div>
                    </CardHeader>
                    <CardContent className={cn("flex grow flex-col items-center justify-center bg-background-primary p-3", cardContentClass)}>
                        {!disableOptimisticDiscountPricing && isPriceUpdatingWithoutOptimism ? (
                            // Original behavior for forms with optimistic pricing enabled.
                            <LoaderWithText spinnerSize="sm" text={Strings.CALCULATING} className="gap-1 text-sm" />
                        ) : (
                            <div ref={priceRef}>
                                {isMultiPetActive ? (
                                    <PriceInfo
                                        variant="all-pets-card"
                                        petId={activePetId}
                                        totalPrice={priceInfoData.roundedPrice}
                                        discountsAmount={priceInfoData.discountsAmount}
                                        policiesCount={policiesCount}
                                        isCalculating={isPriceCalculating}
                                    />
                                ) : (
                                    <PriceInfo
                                        variant="single-pet-card"
                                        petId={pet.id}
                                        totalPrice={priceInfoData.roundedPrice}
                                        discountsAmount={priceInfoData.discountsAmount}
                                        policiesCount={policiesCount}
                                        isCalculating={isPriceCalculating}
                                    />
                                )}
                            </div>
                        )}
                    </CardContent>
                </Card>
            </button>
        </div>
    );
};

export const PlaceholderPetCard = ({ pet, bgColor }: { pet: Policy; bgColor: string }) => (
    <div className="flex size-full flex-col overflow-hidden !rounded-3xl border-0 text-left sm:w-[328px]">
        <div className={cn("flex size-full flex-col !rounded-3xl border border-stroke-primary", bgColor)}>
            <div className="flex shrink-0 flex-row items-center justify-between whitespace-nowrap border-0 bg-background-primary-invert p-3 text-content-primary-invert">
                <div className="flex min-w-0 flex-1 items-center gap-2">
                    <FontAwesomeIcon icon={pet.species === "Cat" ? faCat : faDog} className="size-8 shrink-0" />
                    <span className="truncate font-bold">{pet.name}</span>
                    {/* Placeholder element needed for text truncate to work properly. */}
                    <div className="ml-auto w-24 shrink-0"></div>
                </div>
                <Button disabled variant="invert" className="ml-1 shrink-0 text-xs font-medium leading-5" startDecorator={<FontAwesomeIcon icon={faPencil} className="size-5" />}>
                    {Strings.EDIT_PET}
                </Button>
            </div>
            <div className="flex grow flex-col items-center justify-center p-3">
                <div className="text-center">
                    <div className="text-2xl font-bold">${pet.basePrice?.value}/mo</div>
                    {pet.discountAmount?.value !== undefined && pet.discountAmount.value > 0 && (
                        <div className="text-xs sm:place-self-end">
                            <Badge variant="secondary">{Strings.DISCOUNTS_APPLIED}</Badge>
                        </div>
                    )}
                </div>
            </div>
        </div>
    </div>
);
export default PetCard;
